import { LobbyPlayerState, type Lobby } from "@uno/protocol";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Avatar from "component/Ui/Avatar";
import LevelBadge from "component/Ui/LevelBadge";
import getPublicAssetUrl from "utils/PublicAsset";

type PlayerSlotProps = {
    player: Lobby["players"][number];
    isMe: boolean;
    isHost?: boolean;
};

const PlayerSlot = ({ player, isMe, isHost }: PlayerSlotProps) => {
    const { t } = useTranslation();
    const isReady = player.lobbyState === LobbyPlayerState.Ready;
    const name = player.username || player.id;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isReady ? 1 : 0.6 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="flex flex-col items-center gap-1.5 md:gap-2 viewport-landscape:gap-[clamp(0.25rem,1vh,0.75rem)]"
        >
            <div className="relative">
                {isHost && (
                    <div className="absolute -top-3.5 md:-top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                        <motion.svg
                            className="w-5 h-5 md:w-7 md:h-7 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                            viewBox="0 0 24 24"
                            fill="#fbbf24"
                            stroke="#78350f"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                            animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 3 }}
                        >
                            <path d="M3 7l4 10h10l4-10-6 4-3-6-3 6-6-4z" />
                            <circle cx="3" cy="7" r="1.4" fill="#fbbf24" stroke="#78350f" strokeWidth="1" />
                            <circle cx="21" cy="7" r="1.4" fill="#fbbf24" stroke="#78350f" strokeWidth="1" />
                            <circle cx="12" cy="4" r="1.4" fill="#fbbf24" stroke="#78350f" strokeWidth="1" />
                        </motion.svg>
                    </div>
                )}
                <Avatar src={player.avatar} isReady={isReady} className="w-14 h-14 xs:w-16 xs:h-16 md:w-24 md:h-24 viewport-landscape:w-[clamp(4.5rem,16vh,7rem)] viewport-landscape:h-[clamp(4.5rem,16vh,7rem)]" />
                <LevelBadge level={player.level} className="bottom-0.5 right-0.5 w-5 h-5 md:w-7 md:h-7 text-[9px] md:text-xs" />
            </div>
            <div className="h-[18px] flex items-center justify-center">
                {isMe ? (
                    <div className="you-badge relative overflow-hidden rounded-full px-2 md:px-2.5 py-[2px] flex items-center justify-center">
                        <span className="relative z-10 text-[10px] md:text-xs font-extrabold uppercase tracking-wider text-black drop-shadow-[0_1px_0_rgba(255,255,255,0.3)]">{t("lobby.me")}</span>
                    </div>
                ) : (
                    <span className="flex items-center gap-1 max-w-[70px] md:max-w-[100px]">
                        {player.flag && <img src={getPublicAssetUrl(`assets/flags/${player.flag}.svg`)} alt="" className="w-3.5 h-2.5 shrink-0 object-contain" draggable={false} />}
                        <span className="text-white font-bold text-[10px] md:text-xs uppercase tracking-wide truncate">{name}</span>
                    </span>
                )}
            </div>
        </motion.div>
    );
};

export default PlayerSlot;
